/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react';
import { Box, Button, Typography, IconButton } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { colors } from '../theme/colors';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

const GOLD = '#d4af37';

interface SliderImage {
  id: string;
  imageUrl: string;
  title?: string;
  description?: string;
  order: number;
}



const SLIDER_CACHE_KEY = 'slider_images_cache';

// Preload images so they're ready before the slider renders
function preloadImages(images: { image: string }[]): Promise<void[]> {
  return Promise.all(
    images.map(
      (img) =>
        new Promise<void>((resolve) => {
          if (img.image.startsWith('data:')) {
            resolve();
            return;
          }
          const el = new Image();
          el.onload = () => resolve();
          el.onerror = () => resolve(); // don't block on broken images
          el.src = img.image;
        })
    )
  );
}

export default function ImageSlider() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [visibleSlide, setVisibleSlide] = useState(0);
  const [, setDirection] = useState<'left' | 'right'>('right');
  const [sliderImages, setSliderImages] = useState<any[]>(() => {
    try {
      const cached = localStorage.getItem(SLIDER_CACHE_KEY);
      return cached ? JSON.parse(cached) : [];
    } catch {
      return [];
    }
  });
  const [imagesReady, setImagesReady] = useState(false);
  const [paused, setPaused] = useState(false);

  // Show slider quickly — preload first image with a timeout fallback
  useEffect(() => {
    if (sliderImages.length > 0 && !imagesReady) {
      let resolved = false;
      const done = () => { if (!resolved) { resolved = true; setImagesReady(true); } };
      const firstImg = new Image();
      firstImg.onload = done;
      firstImg.onerror = done;
      firstImg.src = sliderImages[0].image;
      // Don't block longer than 2s even if image is slow
      const timeout = setTimeout(done, 2000);
      preloadImages(sliderImages.slice(1));
      return () => clearTimeout(timeout);
    }
  }, [sliderImages, imagesReady]);

  useEffect(() => { fetchSliderImages(); }, []);

  const fetchSliderImages = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'slider_images'));
      if (!querySnapshot.empty) {
        const fetchedImages: any[] = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data() as SliderImage;
          fetchedImages.push({
            id: doc.id,
            title: data.title || '',
            subtitle: data.description || '',
            color: colors.primary,
            image: data.imageUrl.startsWith('data:')
              ? data.imageUrl
              : data.imageUrl.startsWith('http')
                ? data.imageUrl
                : `${process.env.PUBLIC_URL}${data.imageUrl}`,
            overlay: 'rgba(0,0,0,0.45)',
            isMain: data.order === 0,
            order: data.order,
          });
        });
        const sortedImages = fetchedImages.sort((a, b) => a.order - b.order);
        // Preload first image before updating state
        // Update state immediately — the preload effect will handle readiness
        setSliderImages(sortedImages);
        setImagesReady(false); // trigger preload effect for fresh data
        try {
          localStorage.setItem(SLIDER_CACHE_KEY, JSON.stringify(sortedImages));
        } catch { /* ignore storage full errors */ }
      }
    } catch (error) {
      console.error('Error fetching slider images:', error);
    }
  };

  const goTo = useCallback((idx: number, dir: 'left' | 'right') => {
    setDirection(dir);
    setCurrentSlide(idx);
  }, []);

  const next = useCallback(() => {
    setCurrentSlide(prev => {
      const nextIdx = (prev + 1) % sliderImages.length;
      setDirection('right');
      return nextIdx;
    });
  }, [sliderImages.length]);

  const prev = useCallback(() => {
    setCurrentSlide(prev => {
      const prevIdx = (prev - 1 + sliderImages.length) % sliderImages.length;
      setDirection('left');
      return prevIdx;
    });
  }, [sliderImages.length]);

  useEffect(() => {
    if (paused) return;
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [next, paused]);

  useEffect(() => {
    const t = setTimeout(() => setVisibleSlide(currentSlide), 60);
    return () => clearTimeout(t);
  }, [currentSlide]);

  const slide = sliderImages[visibleSlide];
  const isMain = slide?.isMain;

  if (sliderImages.length === 0 || !imagesReady) {
    return (
      <Box sx={{
        position: 'relative', width: '100%',
        height: { xs: 520, sm: 600, md: 700, lg: 790 },
        bgcolor: '#0a0806', mt: -15,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden',
      }}>
        {/* Shimmer background */}
        <Box sx={{
          position: 'absolute', inset: 0,
          background: `linear-gradient(110deg, #0a0806 30%, #1a1510 38%, #1a1510 40%, #0a0806 48%)`,
          backgroundSize: '200% 100%',
          animation: 'shimmer 1.8s ease-in-out infinite',
          '@keyframes shimmer': {
            '0%': { backgroundPosition: '200% 0' },
            '100%': { backgroundPosition: '-200% 0' },
          },
        }} />
        {/* Temple name & loading indicator */}
        <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center', px: 3 }}>
          <Typography sx={{
            fontSize: { xs: '1.4rem', md: '2.2rem' },
            fontWeight: 800, color: GOLD, letterSpacing: 1,
            textShadow: `0 0 30px ${GOLD}30`,
            mb: 2,
          }}>
            Muchukunnu Sri Kotta-Kovilakam Kshethram
          </Typography>
          <Box sx={{
            width: 40, height: 3, borderRadius: 2, mx: 'auto',
            background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`,
            animation: 'pulse 1.5s ease-in-out infinite',
            '@keyframes pulse': {
              '0%, 100%': { opacity: 0.4 },
              '50%': { opacity: 1 },
            },
          }} />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{ position: 'relative', width: '100%', bgcolor: '#0a0806', mt: -15 }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <Box sx={{
        position: 'relative',
        height: { xs: 520, sm: 600, md: 700, lg: 790 },
        overflow: 'hidden', width: '100%', bgcolor: '#0a0806',
      }}>

        {/* ── Background images with Ken Burns effect ── */}
        {sliderImages.map((image, idx) => {
          const isActive = idx === visibleSlide;
          return (
            <Box
              key={image.id}
              sx={{
                position: 'absolute', inset: 0,
                opacity: isActive ? 1 : 0,
                transition: 'opacity 1.2s cubic-bezier(0.4,0,0.2,1)',
                zIndex: isActive ? 1 : 0,
                '&::before': {
                  content: '""',
                  position: 'absolute', inset: 0,
                  backgroundImage: `url(${image.image})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  animation: isActive ? 'kenBurns 12s ease-in-out infinite alternate' : 'none',
                  transform: 'scale(1)',
                },
                '@keyframes kenBurns': {
                  '0%': { transform: 'scale(1) translate(0, 0)' },
                  '100%': { transform: 'scale(1.08) translate(-0.5%, -0.5%)' },
                },
              }}
            />
          );
        })}

        {/* ── Cinematic overlays ── */}
        <Box sx={{ position: 'absolute', inset: 0, zIndex: 2, pointerEvents: 'none' }}>
          {/* Top header gradient */}
          <Box sx={{
            position: 'absolute', top: 0, left: 0, right: 0, height: '25%',
            background: 'linear-gradient(180deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.15) 60%, transparent 100%)',
          }} />
          {/* Bottom gradient */}
          <Box sx={{
            position: 'absolute', bottom: 0, left: 0, right: 0, height: '55%',
            background: 'linear-gradient(0deg, rgba(10,8,6,1) 0%, rgba(10,8,6,0.85) 25%, rgba(0,0,0,0.3) 60%, transparent 100%)',
          }} />
          {/* Left side accent gradient for text readability */}
          <Box sx={{
            position: 'absolute', top: 0, left: 0, bottom: 0, width: '65%',
            background: 'linear-gradient(90deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.2) 40%, transparent 100%)',
            display: { xs: 'none', md: 'block' },
          }} />
          {/* Main dark scrim */}
          <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.25)' }} />
          {/* Gold shimmer at bottom */}
          <Box sx={{
            position: 'absolute', bottom: 0, left: 0, right: 0, height: 3,
            background: `linear-gradient(90deg, transparent 5%, ${GOLD}50 20%, ${GOLD}80 50%, ${GOLD}50 80%, transparent 95%)`,
            opacity: 0.6,
          }} />
        </Box>

        {/* ── Content ── */}
        <Box sx={{
          position: 'absolute', inset: 0, zIndex: 3,
          display: 'flex', alignItems: 'center',
          px: { xs: 3, sm: 4, md: 8, lg: 12 },
          pt: { xs: 8, md: 6 },
        }}>
          <Box sx={{
            maxWidth: { xs: '100%', md: isMain ? 700 : 600 },
            textAlign: { xs: 'center', md: 'left' },
            mx: { xs: 'auto', md: 0 },
          }}>
            {/* Slide counter badge */}
            <Box sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.8,
              px: 1.8, py: 0.5, mb: 2.5,
              borderRadius: 20,
              bgcolor: `${GOLD}15`,
              border: `1px solid ${GOLD}30`,
              backdropFilter: 'blur(8px)',
            }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: GOLD, boxShadow: `0 0 8px ${GOLD}` }} />
              <Typography sx={{ fontSize: '0.72rem', color: GOLD, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase' }}>
                {String((visibleSlide || 0) + 1).padStart(2, '0')} / {String(sliderImages.length || 0).padStart(2, '0')}
              </Typography>
            </Box>

            {/* Title */}
            <Typography
              key={`title_${visibleSlide}`}
              sx={{
                fontWeight: 900,
                fontSize: { xs: '1.6rem', sm: '2rem', md: isMain ? '2.8rem' : '2.4rem', lg: isMain ? '3.2rem' : '2.8rem' },
                lineHeight: 1.12,
                color: '#fff',
                mb: isMain ? 2 : 1.5,
                textShadow: '0 4px 20px rgba(0,0,0,0.5), 0 1px 3px rgba(0,0,0,0.3)',
                letterSpacing: '-0.01em',
                animation: 'slideTextIn 0.8s cubic-bezier(0.16,1,0.3,1) both',
                '@keyframes slideTextIn': {
                  '0%': { opacity: 0, transform: 'translateY(30px)' },
                  '100%': { opacity: 1, transform: 'translateY(0)' },
                },
              }}
            >
              {slide?.title}
            </Typography>

            {/* Subtitle — only on main slide */}
            {isMain && slide?.subtitle && (
              <Typography
                key={`sub_${visibleSlide}`}
                sx={{
                  fontSize: { xs: '0.95rem', md: '1.15rem' },
                  color: 'rgba(255,255,255,0.75)',
                  fontWeight: 400, lineHeight: 1.6,
                  mb: 3.5,
                  maxWidth: 520,
                  mx: { xs: 'auto', md: 0 },
                  textShadow: '0 2px 8px rgba(0,0,0,0.4)',
                  animation: 'slideTextIn 0.8s 0.15s cubic-bezier(0.16,1,0.3,1) both',
                }}
              >
                {slide.subtitle}
              </Typography>
            )}

            {/* Decorative line */}
            <Box sx={{
              width: 60, height: 3, borderRadius: 2,
              background: `linear-gradient(90deg, ${GOLD}, ${GOLD}60)`,
              mb: 3.5,
              mx: { xs: 'auto', md: 0 },
              boxShadow: `0 0 12px ${GOLD}40`,
              animation: 'lineGrow 0.6s 0.3s cubic-bezier(0.16,1,0.3,1) both',
              '@keyframes lineGrow': {
                '0%': { width: 0, opacity: 0 },
                '100%': { width: 60, opacity: 1 },
              },
            }} />

            {/* CTA Buttons — temporarily hidden
            <Box sx={{
              display: 'flex', gap: 2,
              justifyContent: { xs: 'center', md: 'flex-start' },
              flexWrap: 'wrap',
              animation: 'slideTextIn 0.8s 0.35s cubic-bezier(0.16,1,0.3,1) both',
            }}>
              <Button
                variant="contained"
                component={RouterLink}
                to="/donate"
                sx={{
                  bgcolor: GOLD, color: '#0a0806',
                  fontWeight: 800, px: 4, py: 1.4,
                  fontSize: '0.95rem', borderRadius: 2,
                  letterSpacing: 0.5,
                  boxShadow: `0 8px 28px ${GOLD}45, 0 2px 8px rgba(0,0,0,0.3)`,
                  border: `1px solid ${GOLD}`,
                  transition: 'all 250ms cubic-bezier(0.4,0,0.2,1)',
                  '&:hover': {
                    bgcolor: '#e5c158', transform: 'translateY(-2px)',
                    boxShadow: `0 12px 36px ${GOLD}55, 0 4px 12px rgba(0,0,0,0.4)`,
                  },
                }}
              >
                Donate Now
              </Button>
              <Button
                variant="outlined"
                component={RouterLink}
                to="/poojas"
                sx={{
                  color: '#fff',
                  borderColor: 'rgba(255,255,255,0.4)',
                  borderWidth: 1.5,
                  fontWeight: 700, px: 4, py: 1.4,
                  fontSize: '0.95rem', borderRadius: 2,
                  letterSpacing: 0.5,
                  backdropFilter: 'blur(8px)',
                  bgcolor: 'rgba(255,255,255,0.06)',
                  transition: 'all 250ms cubic-bezier(0.4,0,0.2,1)',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.15)',
                    borderColor: GOLD,
                    color: GOLD,
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                Book a Pooja
              </Button>
            </Box>
            */}
          </Box>
        </Box>

        {/* ── Navigation arrows ── */}
        {[
          { icon: <ArrowBackIosNewIcon sx={{ fontSize: 18 }} />, onClick: prev, pos: 'left' },
          { icon: <ArrowForwardIosIcon sx={{ fontSize: 18 }} />, onClick: next, pos: 'right' },
        ].map((arrow) => (
          <IconButton
            key={arrow.pos}
            onClick={arrow.onClick}
            sx={{
              position: 'absolute',
              top: '50%', transform: 'translateY(-50%)',
              [arrow.pos]: { xs: 8, md: 24 },
              zIndex: 5,
              width: { xs: 44, md: 48 }, height: { xs: 44, md: 48 },
              color: 'rgba(255,255,255,0.7)',
              bgcolor: 'rgba(0,0,0,0.3)',
              backdropFilter: 'blur(12px)',
              border: `1px solid rgba(255,255,255,0.1)`,
              transition: 'all 250ms ease',
              '&:hover': {
                bgcolor: `${GOLD}20`,
                color: GOLD,
                borderColor: `${GOLD}40`,
                boxShadow: `0 0 20px ${GOLD}20`,
              },
            }}
          >
            {arrow.icon}
          </IconButton>
        ))}

        {/* ── Bottom slide indicators ── */}
        <Box sx={{
          position: 'absolute',
          bottom: { xs: 24, md: 36 },
          right: { xs: '50%', md: 64 },
          transform: { xs: 'translateX(50%)', md: 'none' },
          zIndex: 5,
          display: 'flex', alignItems: 'center', gap: 1,
        }}>
          {sliderImages.map((_, idx) => (
            <Box
              key={idx}
              onClick={() => goTo(idx, idx > visibleSlide ? 'right' : 'left')}
              sx={{
                position: 'relative',
                width: idx === visibleSlide ? 32 : 8,
                height: 4,
                borderRadius: 2,
                bgcolor: idx === visibleSlide ? GOLD : 'rgba(255,255,255,0.3)',
                boxShadow: idx === visibleSlide ? `0 0 10px ${GOLD}60` : 'none',
                transition: 'all 400ms cubic-bezier(0.4,0,0.2,1)',
                cursor: 'pointer',
                '&::before': {
                  content: '""', position: 'absolute',
                  top: -20, bottom: -20, left: -8, right: -8,
                },
                '&:hover': {
                  bgcolor: idx === visibleSlide ? GOLD : 'rgba(255,255,255,0.55)',
                },
              }}
            />
          ))}
        </Box>

        {/* ── Scroll hint ── */}
        <Box sx={{
          position: 'absolute',
          bottom: { xs: 24, md: 36 },
          left: { xs: '50%', md: 64 },
          transform: { xs: 'translateX(-50%)', md: 'none' },
          zIndex: 5,
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center', gap: 1.2,
        }}>
          <Box sx={{
            width: 20, height: 32, borderRadius: 10,
            border: '2px solid rgba(255,255,255,0.25)',
            display: 'flex', justifyContent: 'center', pt: 0.6,
          }}>
            <Box sx={{
              width: 3, height: 8, borderRadius: 2, bgcolor: GOLD,
              animation: 'scrollBounce 2s ease-in-out infinite',
              '@keyframes scrollBounce': {
                '0%, 100%': { transform: 'translateY(0)', opacity: 1 },
                '50%': { transform: 'translateY(8px)', opacity: 0.3 },
              },
            }} />
          </Box>
          <Typography sx={{
            fontSize: '0.68rem', color: 'rgba(255,255,255,0.35)',
            fontWeight: 600, letterSpacing: 2, textTransform: 'uppercase',
          }}>
            Scroll
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
